package music.jammers.client.shared.event.track.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.consumeAsFlow
import music.jammers.client.shared.analytics.event.EventAnalytics
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.track.domain.usecase.DeleteEventTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.GetEventTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.SignInTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.SignOutTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.UpdateEventTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.ValidateEventTrackUseCase
import music.jammers.client.shared.event.track.domain.validator.EventTrackFieldValidator
import music.jammers.client.shared.event.track.presentation.model.UpdateInstrument
import music.jammers.client.shared.event.track.presentation.model.toEventInstrumentList
import music.jammers.shared.event.domain.model.EventMember
import music.jammers.shared.event.track.domain.model.EventTrack
import music.jammers.shared.event.track.domain.model.UpdateEventTrack
import music.jammers.shared.user.domain.model.User
import music.limasoul.client.shared.event.domain.usecase.GetEventDetailsUseCase
import music.limasoul.client.shared.navigation.Navigation
import music.limasoul.client.shared.navigation.Router
import music.limasoul.client.shared.user.domain.usecase.GetCurrentUserUseCase

class EventTrackDetailsViewModel(
    private val getEventTrackUseCase: GetEventTrackUseCase,
    private val updateEventTrackUseCase: UpdateEventTrackUseCase,
    private val deleteEventTrackUseCase: DeleteEventTrackUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val eventTrackFieldValidator: EventTrackFieldValidator,
    private val validateEventTrackUseCase: ValidateEventTrackUseCase,
    private val signInTrackUseCase: SignInTrackUseCase,
    private val signOutTrackUseCase: SignOutTrackUseCase,
    private val getEventDetailsUseCase: GetEventDetailsUseCase,
    private val eventAnalytics: EventAnalytics,
    private val router: Router,
    initState: EventTrackDetailsState = EventTrackDetailsState(),
) : BaseViewModel() {

    internal val state: State<EventTrackDetailsState> get() = _state
    internal val editModeState: State<Boolean> get() = _editModeState
    internal val validationState: State<FieldValidationState> get() = _validationState
    internal val openUrlEvent: Flow<String> get() = _openUrlEvent.consumeAsFlow()

    internal val snackBarState: Flow<Unit> by lazy {
        _snackBarState.consumeAsFlow()
    }

    private val _state = mutableStateOf(initState)
    private val _editModeState = mutableStateOf(false)
    private val _validationState = mutableStateOf(FieldValidationState())
    private val _openUrlEvent = Channel<String>()
    private val _snackBarState = Channel<Unit>()

    private val errorHandler = customExceptionHandler(router) { _, throwable ->
        _state.update {
            copy(
                progress = false,
                error = throwable,
            )
        }
    }

    internal fun onBind(eventId: Long, eventTrackId: Long) {
        _state.update {
            copy(
                progress = true,
                eventId = eventId,
                eventTrackId = eventTrackId,
            )
        }

        launch(errorHandler) {
            val user = getCurrentUserUseCase()

            _state.update {
                copy(
                    user = user,
                )
            }

            loadTrackData()
        }
    }

    private suspend fun loadTrackData() {
        val current = state.value.eventTrackId
        val eventTrack = getEventTrackUseCase.invoke(current)
        updateState(eventTrack)
    }

    private fun updateState(eventTrack: EventTrack) {
        _state.update {
            copy(
                progress = false,
                eventTrackId = eventTrackId,
                artistName = eventTrack.track.artist.name,
                trackTitle = eventTrack.track.name,
                comment = eventTrack.comment ?: "",
                addedBy = eventTrack.addedBy,
                members = eventTrack.members,
                selectedInstruments = emptyList(),
            )
        }
    }

    internal fun onBackPressed() {
        router.back()
    }

    internal fun onInstrumentClicked(updateInstrument: UpdateInstrument) {
        val nextStatus = when (updateInstrument.status.value) {
            UpdateInstrument.Status.REQUIRED -> UpdateInstrument.Status.OPTIONAL
            UpdateInstrument.Status.OPTIONAL -> UpdateInstrument.Status.NOT_REQUIRED
            UpdateInstrument.Status.NOT_REQUIRED -> UpdateInstrument.Status.REQUIRED
        }

        updateInstrument.status.value = nextStatus

        validateInstruments(_state.value.selectedInstruments)
    }

    internal fun onErrorShown() {
        _state.update {
            copy(error = null)
        }
    }

    internal fun onCommentChanged(value: String) {
        _state.update {
            copy(comment = value)
        }
    }

    internal fun onApplyClicked() {
        val currentState = state.value

        val errors = validateEventTrackUseCase.invoke(
            artistName = currentState.artistName,
            trackName = currentState.trackTitle,
            instruments = currentState.selectedInstruments,
        )

        if (errors.isEmpty()) {
            val updateEventMemberList = currentState.selectedInstruments.toEventInstrumentList()

            val updateEventTrack = UpdateEventTrack(
                comment = currentState.comment,
                updateMembers = updateEventMemberList,
            )

            saveTrack(currentState.eventTrackId, updateEventTrack)
        } else {
            _validationState.update {
                copy(
                    validationErrorMap = errors.associateBy { item ->
                        item.fieldName
                    }
                )
            }
        }
    }

    private fun saveTrack(trackId: Long, updateEventTrack: UpdateEventTrack) {
        _state.update {
            copy(
                progress = true,
            )
        }

        launch(errorHandler) {
            val track = updateEventTrackUseCase.invoke(trackId, updateEventTrack)

            _editModeState.value = false

            updateState(track)
        }
    }

    internal fun onEditModeClicked() {
        _editModeState.value = true

        launch(errorHandler) {
            val eventDetails = getEventDetailsUseCase(state.value.eventId)
            val availableInstruments = eventDetails.eventInstrumentList.toSet()
            val currentInstruments = state.value.members.map { item -> item.instrumentType }.toSet()

            val remain = availableInstruments - currentInstruments

            val currentUpdateData = state.value.members.map { item ->
                val status = when (item.defaultStatus) {
                    EventMember.Status.OPTIONAL -> UpdateInstrument.Status.OPTIONAL
                    else -> UpdateInstrument.Status.REQUIRED
                }

                UpdateInstrument(
                    eventInstrument = item.instrumentType,
                    status = mutableStateOf(status),
                )
            }

            val availableUpdateInstruments = remain.map { item ->
                UpdateInstrument(
                    eventInstrument = item,
                    status = mutableStateOf(UpdateInstrument.Status.NOT_REQUIRED)
                )
            }

            val selectedInstrumentList = (currentUpdateData + availableUpdateInstruments).sortedBy { item ->
                item.eventInstrument.id
            }

            _state.update {
                copy(
                    selectedInstruments = selectedInstrumentList
                )
            }
        }
    }

    internal fun onCloseEditModeClicked() {
        _editModeState.value = false

        _state.update {
            copy(selectedInstruments = emptyList())
        }

        _validationState.update {
            FieldValidationState()
        }
    }

    internal fun onInstrumentClicked(eventMember: EventMember) {
        when {
            isCurrentUserMember(eventMember) -> {
                eventAnalytics.onCancelMember(eventMember.id)
                _state.update {
                    copy(
                        confirmSignOutTrack = eventMember
                    )
                }
            }

            eventMember.isAvailable -> {
                eventAnalytics.onAcceptMember(eventMember.id)
                _state.update {
                    copy(
                        confirmSignInTrack = eventMember,
                    )
                }
            }
        }
    }

    fun onDeleteTrackClicked() {
        _state.update {
            copy(
                deleteDialogOpened = true,
            )
        }
    }

    fun onDeleteTrackDialogOkPressed() {
        _state.update {
            copy(deleteDialogOpened = false, progress = true)
        }

        launch(errorHandler) {
            deleteEventTrackUseCase(state.value.eventTrackId)
            router.back()
        }
    }

    fun onDeleteTrackDialogCancelButtonClicked() {
        _state.update {
            copy(
                deleteDialogOpened = false,
            )
        }
    }

    private fun validateInstruments(selected: List<UpdateInstrument>) {
        _validationState.update {
            val error = eventTrackFieldValidator.validate(selected)
            if (error == null) {
                resetError(ValidateEventUseCase.SELECTED_INSTRUMENTS)
            } else {
                addError(error)
            }
        }
    }

    fun onSignInPositive(eventMember: EventMember) {
        eventAnalytics.onSignInSuccess(eventMember.id)

        _state.update {
            copy(
                progress = true
            )
        }

        launch(errorHandler) {
            val member = signInTrackUseCase(eventMember)

            if (member.status == EventMember.Status.BOOKED_OPTIONAL) {
                _snackBarState.trySend(Unit)
            }

            _state.update {
                copy(
                    progress = false,
                    confirmSignInTrack = null
                )
            }

            loadTrackData()
        }
    }

    fun onDialogNegative() {
        _state.update {
            copy(
                confirmSignInTrack = null,
                confirmSignOutTrack = null,
            )
        }
    }

    fun onSignOutPositive(eventMember: EventMember) {
        eventAnalytics.onSignOutSuccess(eventMember.id)

        _state.update {
            copy(
                progress = true
            )
        }

        launch(errorHandler) {
            signOutTrackUseCase(eventMember)

            _state.update {
                copy(
                    confirmSignOutTrack = null
                )
            }

            loadTrackData()
        }
    }

    private fun isCurrentUserMember(eventMember: EventMember): Boolean =
        state.value.user?.let { currentUser ->
            currentUser == eventMember.user
        } ?: false

    internal fun onUserClicked(user: User) {
        if (user == _state.value.user) {
            router.navigateTo(Navigation.Profile)
        } else {
            router.navigateTo(Navigation.ProfileUser(user.id))
        }
    }

    internal fun onUrlClick(value: String) {
        launch { _openUrlEvent.send(value) }
    }
}